import React from 'react'
import {useIntl, defineMessages} from 'react-intl'
import {Box, BoxProps, HStack, StackProps, useBreakpointValue, useMultiStyleConfig} from '@chakra-ui/react'
import {HeaderLink, HideBelowViewport, HideFromViewport} from '_uiComponents'
import {HeaderActionType, useGlobalConfigState, useHeaderDispatch, useHeaderState} from '_context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useAnalytics, useOpenModal} from '_customHooks'
import {countryLabels} from '_utils/data'

interface TopbarLeftProps {
    isInSidebar?: boolean
}

/**
 * @name TopbarLeft
 *
 * @component
 *
 * @description
 * Renders a list of header links on desktop and a burger button on mobile.
 *
 * @remarks
 * The sidebar content is the same as the header desktop version but with different styles.
 *
 * @param {TopbarLeftProps} props - Properties object.
 * @param {boolean} props.isInSidebar - Indicates if the topbar left is rendered inside the sidebar.
 *
 * @returns {JSX.Element} - Rendered topbar left component.
 */
const TopbarLeft = ({isInSidebar = false}: TopbarLeftProps): JSX.Element => {
    const {formatMessage} = useIntl()
    const isMobile = useBreakpointValue({base: true, xl: false})
    const {locale} = useMultiSite()
    const {openNewsletterModal, openLocaleModal} = useOpenModal()
    const {analyticsEventNewsletter, analyticsEventChangeLocale} = useAnalytics()
    const {appPreferences} = useGlobalConfigState()
    const {customNumberContact} = appPreferences

    const {isSidebarOpen, isTransparentMode, isAtTop} = useHeaderState()
    const {dispatch} = useHeaderDispatch()

    const styles = useMultiStyleConfig('Topbar', {isTransparentMode, isAtTop, isInSidebar}) as Record<string, BoxProps & StackProps>
    const colorMode = isTransparentMode ? 'light' : 'dark'

    const countryCode = locale.id.split('-')[1]
    const countryName = formatMessage(countryLabels[countryCode])

    const phoneNumbers = customNumberContact ? JSON.parse(customNumberContact) : undefined

    const retrievePhoneNumber = (): {label: string; value: string} | undefined => {
        const localeId = locale.id.split('-')[1]

        if (phoneNumbers && phoneNumbers[localeId] !== undefined) {
            return phoneNumbers[localeId]
        } else {
            return undefined
        }
    }

    const phone = retrievePhoneNumber()
    const phoneHref = isMobile ? `tel:${phone?.value}` : undefined

    const languageLabel = formatMessage({id: 'alt.link.language', defaultMessage: 'Change Country/Language'})
    const contactUsLabel = formatMessage({id: 'label.contactUs.title', defaultMessage: 'Contact Us'})
    const boutiqueLabel = formatMessage({id: 'label.link.boutique', defaultMessage: 'Boutique'})
    const newsletterLabel = formatMessage({id: 'label.newsletter', defaultMessage: 'Newsletter'})
    const phoneNumberLabel = formatMessage({id: 'accessibility.label.menu.telephone', defaultMessage: 'Telephone:'})

    const menuLabels = defineMessages({
        openMenu: {id: 'label.menu.open', defaultMessage: 'Open menu'},
        closeMenu: {id: 'label.menu.close', defaultMessage: 'Close menu'},
    })

    const openLanguageModal = () => {
        analyticsEventChangeLocale({action: 'start', label: locale.id.replace('-', '_')})

        openLocaleModal({
            onClose: () => {
                analyticsEventChangeLocale({
                    action: 'close',
                    label: locale.id.replace('-', '_'),
                })
            },
        })
    }

    const triggerNewsletterModal = () => {
        analyticsEventNewsletter({action: 'start', label: 'header_menu'})

        openNewsletterModal({
            analyticsLabel: 'header_menu',
            analyticsEventNewsletter: () => analyticsEventNewsletter({action: 'dismiss', label: 'header_menu'}),
        })
    }

    const handleBurgerClick = () => {
        dispatch({type: HeaderActionType.TOGGLE_SIDEBAR})
    }

    /**
     * @name HeaderLinks
     *
     * @component
     *
     * @description
     * Modularized list of header links to render properly in both header and sidebar.
     *
     * @returns {JSX.Element} - List of header links.
     */
    const HeaderLinks = (): JSX.Element => {
        return (
            <>
                <HeaderLink
                    iconName="IconLanguage"
                    colorMode={colorMode}
                    shouldShowText={isAtTop}
                    ariaLabel={languageLabel}
                    isInSidebar={isInSidebar}
                    onClick={openLanguageModal}
                >
                    {countryName}
                </HeaderLink>

                <HeaderLink
                    href={`/${locale.alias}/contactus`}
                    iconName="IconAssistance"
                    colorMode={colorMode}
                    shouldShowText={isAtTop}
                    isInSidebar={isInSidebar}
                    ariaLabel={contactUsLabel}
                    isExternal
                >
                    {contactUsLabel}
                </HeaderLink>

                <HeaderLink
                    href={`/${locale.alias}/findboutiques`}
                    iconName="IconMaps"
                    colorMode={colorMode}
                    shouldShowText={isAtTop}
                    isInSidebar={isInSidebar}
                    ariaLabel={boutiqueLabel}
                    isExternal
                >
                    {boutiqueLabel}
                </HeaderLink>

                {phone && (
                    <HeaderLink
                        href={phoneHref}
                        iconName="IconContact"
                        colorMode={colorMode}
                        isExternal={true}
                        isAtTop={isAtTop}
                        isInSidebar={isInSidebar}
                        ariaLabel={`${phoneNumberLabel} ${phone?.label}`}
                    >
                        {phone.label}
                    </HeaderLink>
                )}

                <HeaderLink
                    iconName="IconEmailSmall"
                    colorMode={colorMode}
                    shouldShowText={isAtTop}
                    isInSidebar={isInSidebar}
                    onClick={triggerNewsletterModal}
                    ariaLabel={newsletterLabel}
                >
                    {newsletterLabel}
                </HeaderLink>
            </>
        )
    }

    return (
        <Box data-testid="topbar-left" {...styles.topbarLeftWrapper}>
            {!isInSidebar ? (
                <>
                    <HideFromViewport breakpoint="lg" {...styles.topbarLeft} w="fit-content">
                        <HeaderLink
                            iconName={isSidebarOpen ? 'IconCloseLight' : 'IconMenu'}
                            iconSize="md"
                            colorMode="dark"
                            ariaLabel={isSidebarOpen ? formatMessage(menuLabels.closeMenu) : formatMessage(menuLabels.openMenu)}
                            onClick={handleBurgerClick}
                        />
                    </HideFromViewport>

                    <HideBelowViewport as={HStack} breakpoint="lg" {...styles.topbarLeft}>
                        <HeaderLinks />
                    </HideBelowViewport>
                </>
            ) : (
                <HeaderLinks />
            )}
        </Box>
    )
}

export default TopbarLeft
